import {
  DEFAULT_RETRY_OPTIONS,
  type LighterAccountIndex,
} from '@funkit/api-base'
import { retry } from '@lifeomic/attempt'
import { useQuery } from '@tanstack/react-query'
import { useDynamicConfig } from '../../src/hooks/statsig'
import { LIGHTER_UNSTAKED_FEE_TOKENS } from './lighter'

export type LighterTransferFeeInfo = {
  code: number
  transfer_fee_usdc: number
}

/** USDC is 6 decimals on Lighter's L2 (see `LIGHTER_SECURE_ASSETS_BY_INDEX`). */
const LIGHTER_USDC_DECIMALS = 6

export async function getLighterTransferFeeInfo(
  accountIndex: LighterAccountIndex,
  toAccountIndex: number,
): Promise<LighterTransferFeeInfo> {
  const params = new URLSearchParams({
    account_index: accountIndex,
    to_account_index: String(toAccountIndex),
  })
  // Same retry policy every @funkit/api-base endpoint uses, so a transient
  // blip is absorbed the same way here as everywhere else that calls out.
  return retry(async () => {
    const response = await fetch(
      `https://mainnet.zklighter.elliot.ai/api/v1/transferFeeInfo?${params}`,
    )
    if (!response.ok) {
      throw new Error(
        `Failed to fetch lighter transfer fee info: ${response.statusText}`,
      )
    }
    return response.json()
  }, DEFAULT_RETRY_OPTIONS)
}

/**
 * Resolves the settled fee, in source tokens. Kept as an early-return helper
 * rather than inline ternaries so the three fee states (pending / errored /
 * loaded) read top to bottom.
 *
 * Fails closed: Lighter charges the fee whether or not we could read it, so an
 * unreadable fee falls back to the flat historical amount rather than 0 —
 * quoting the gross amount would promise tokens the transfer won't deliver.
 */
function resolveSettledFeeTokens(
  query: Pick<
    ReturnType<typeof useQuery<LighterTransferFeeInfo>>,
    'isError' | 'data'
  >,
): number {
  if (query.isError) {
    return LIGHTER_UNSTAKED_FEE_TOKENS
  }
  return (query.data?.transfer_fee_usdc ?? 0) / 10 ** LIGHTER_USDC_DECIMALS
}

/**
 * Lighter Fast's unstaked-withdrawal fee, in source tokens (USDC), read
 * straight from Lighter's `transferFeeInfo` endpoint (stake-aware, replaces
 * the deprecated client-computed share-price gate).
 *
 * `to_account_index` is Relay's Lighter operating account for Fast
 * withdrawals — sourced from a dynamic config since Relay can rotate it.
 *
 * `isFetched` stays `false` until the first fee resolves; callers must wait
 * for it before quoting, since `feeTokens` is 0 (not yet the real fee) until then.
 */
export function useLighterFastUnstakedFeeTokens({
  accountIndex,
  enabled,
}: {
  accountIndex: LighterAccountIndex | undefined
  /** Fast only — Secure charges no such fee. */
  enabled: boolean
}): { feeTokens: number; isFetched: boolean; isError: boolean } {
  const toAccountIndex = useDynamicConfig('lighterfasttransferaccountindex')
  const isEnabled = enabled && !!accountIndex
  const query = useQuery({
    // Statsig can hand back the checked-in default before the console value
    // loads, and this key changes when it does — one stale-then-live refetch
    // per account rotation, not a bug this hook needs to guard against.
    queryKey: ['lighterTransferFeeInfo', accountIndex, toAccountIndex],
    queryFn: () => {
      if (!accountIndex) {
        throw new Error('Lighter transfer fee query ran without an account')
      }
      return getLighterTransferFeeInfo(accountIndex, toAccountIndex)
    },
    enabled: isEnabled,
    // Only changes when the account's stake crosses Lighter's threshold, and a
    // withdrawal session doesn't outlive that — one fetch per tab is enough.
    staleTime: Infinity,
    // Retries already happen inside getLighterTransferFeeInfo via
    // @lifeomic/attempt — a second retry layer here would double them up.
    retry: false,
  })

  const isSettled = isEnabled && query.isFetched

  return {
    feeTokens: isSettled ? resolveSettledFeeTokens(query) : 0,
    isFetched: isSettled,
    isError: isSettled && query.isError,
  }
}
